import { Form, Input, Select } from 'antd';
import BottomSheet, {
  SheetSummary,
} from 'components/UI/Responsive/BottomSheet';
import CurrencyInput from 'components/UI/CurrencyInputNew';
import Monetary from 'components/UI/Data/Monetary';
import * as BuyIntentionModels from 'model/BuyIntention';
import { useTranslation } from 'react-i18next';
import { VerticalFormFields } from 'styles/VerticalFormFields';
import { BuyIntentionItemActions } from '../useBuyIntentionItemActions';
import * as S from './styles';

const { Option } = Select;

interface Props {
  item: BuyIntentionModels.BuyIntentionData | undefined;
  actions: BuyIntentionItemActions;
  onClose: () => void;
}

function getPopupContainer(triggerNode: HTMLElement) {
  return triggerNode.parentElement as HTMLElement;
}

export default function BuyIntentionItemSheet({
  item,
  actions,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const {
    currentStep,
    customer,
    changeOfferInput,
    choicePacking,
    choiceProducer,
    changeQuantityPacking,
    stepQuantityPacking,
    changeQuantityUnit,
    changeUnitPrice,
    searchOffers,
    saveIntention,
    handleTotalPrice,
    requestDelete,
  } = actions;

  const handleAdd = () => {
    if (item && searchOffers(item)) onClose();
  };

  const handleDelete = () => {
    if (!item) return;
    requestDelete(item);
    onClose();
  };

  const handleSave = () => {
    if (item && saveIntention(item)) onClose();
  };

  const renderFooter = (data: BuyIntentionModels.BuyIntentionData) => {
    if (currentStep !== 0) {
      return (
        <S.SheetAction danger onClick={handleDelete}>
          Excluir produto
        </S.SheetAction>
      );
    }
    if (data.intention) {
      return (
        <S.SheetActions>
          <S.SheetAction danger onClick={handleDelete}>
            Remover seleção
          </S.SheetAction>
          <S.SheetAction type="primary" onClick={handleSave}>
            Salvar
          </S.SheetAction>
        </S.SheetActions>
      );
    }
    return (
      <S.SheetAction type="primary" disabled={data.bought} onClick={handleAdd}>
        Adicionar produto
      </S.SheetAction>
    );
  };

  const footer = item && renderFooter(item);

  const shippingValue = item?.shippingFeeFilials?.productShippingValue ?? 0;

  return (
    <BottomSheet
      isOpen={Boolean(item)}
      onClose={onClose}
      title={item?.productName ?? ''}
      summary={
        item && (
          <SheetSummary>
            {[item.productCode, item.site?.conceptName]
              .filter(Boolean)
              .join(' · ')}
          </SheetSummary>
        )
      }
      footer={footer}
    >
      {item && (
        <S.SheetFields>
          <Form layout="vertical" component="div">
            <VerticalFormFields>
              <Form.Item label={t('buyIntention.table.producer')}>
                <Select
                  dropdownStyle={{ maxHeight: 200, overflow: 'auto' }}
                  getPopupContainer={getPopupContainer}
                  disabled={item.siteList === null}
                  value={
                    item.site !== null
                      ? `${item.site?.account} - ${item.site?.tradingName}`
                      : undefined
                  }
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption
                  placeholder={
                    item.siteList === null
                      ? `${item.site?.account} - ${item.site?.tradingName}`
                      : 'Selecione'
                  }
                  onChange={(v) => choiceProducer(Number(v), item)}
                >
                  {Array.isArray(item.siteList) &&
                    item.siteList.map((site) => (
                      <Option key={site.id} value={site.id}>
                        {`${site.account} - ${site.tradingName}`}
                      </Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item label={t('buyIntention.table.quality')}>
                <Select
                  getPopupContainer={getPopupContainer}
                  disabled={!item.site}
                  value={item.qualitySelect || undefined}
                  placeholder="Selecione"
                  allowClear={false}
                  onChange={(v) => changeOfferInput(v, 'qualitySelect', item)}
                >
                  {item.qualityList.map((quality) => (
                    <Option key={quality.id} value={quality.code}>
                      {quality.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label={t('buyIntention.table.packing')}>
                <Select
                  getPopupContainer={getPopupContainer}
                  disabled={!item.productPreference}
                  value={item.packingSelected?.code || undefined}
                  placeholder="Selecione"
                  allowClear={false}
                  showSearch
                  optionFilterProp="children"
                  filterOption
                  onChange={(v: string) => choicePacking(v, item)}
                >
                  {item.productPreference &&
                    item.productPreference.map((packing) => (
                      <Option value={packing.code} key={packing.id}>
                        {packing.code} - {packing.abbrev}
                      </Option>
                    ))}
                </Select>
                {item.packingSelected?.additionalCost === 'S' && (
                  <S.FieldHint>
                    <Monetary symbol value={item.packingSelected.packingCost} />
                    /un.
                  </S.FieldHint>
                )}
              </Form.Item>

              <Form.Item label={t('buyIntention.table.qe')}>
                <S.QuantityInput
                  aria-label={t('buyIntention.table.qe')}
                  disabled={!item.packingSelected}
                  inputMode="numeric"
                  value={item.quantityPacking}
                  min={1}
                  step={item.packingSelected?.qe || 1}
                  onStep={(value) => stepQuantityPacking(Number(value), item)}
                  onChange={(value) => changeQuantityPacking(value, item)}
                />
                {item.packingSelected && (
                  <S.FieldHint>
                    {`Múltiplo ${item.packingSelected?.qe || ''}`}
                  </S.FieldHint>
                )}
              </Form.Item>

              <Form.Item label={t('buyIntention.table.qpe')}>
                <S.QuantityInput
                  aria-label={t('buyIntention.table.qpe')}
                  disabled={!item.packingSelected}
                  inputMode="numeric"
                  value={item.quantityUnit}
                  min={1}
                  step={1}
                  onChange={(value) => changeQuantityUnit(value, item)}
                />
              </Form.Item>

              <Form.Item
                label={`${t('buyIntention.table.unitValue')} ${t(
                  'buyIntention.table.noICMS',
                )}`}
              >
                <div className="currency-field">
                  <CurrencyInput
                    disabledOption={!item.packingSelected}
                    id={`sheet-${item.id}`}
                    inputMode="decimal"
                    value={item.unitPrice}
                    onChange={(value) => changeUnitPrice(value, item)}
                  />
                </div>
              </Form.Item>

              {customer?.branch === 'CVH_UB' && (
                <Form.Item label={t('buyIntention.table.shippingFee')}>
                  <Monetary symbol value={shippingValue} />
                  <S.FieldHint>
                    {t('buyIntention.table.shippingTotalFee')}:{' '}
                    <Monetary
                      symbol
                      value={
                        item.quantityPacking * item.quantityUnit * shippingValue
                      }
                    />
                  </S.FieldHint>
                </Form.Item>
              )}

              <Form.Item label={t('buyIntention.table.observation')}>
                <Input
                  aria-label={t('buyIntention.table.observation')}
                  value={item.observationText}
                  onChange={(e) =>
                    changeOfferInput(e.target.value, 'observationText', item)
                  }
                />
              </Form.Item>
            </VerticalFormFields>
          </Form>

          <S.TotalRow>
            <span>{t('buyIntention.table.totalValue')}</span>
            <span>
              <Monetary symbol value={handleTotalPrice(item)} />
            </span>
          </S.TotalRow>
        </S.SheetFields>
      )}
    </BottomSheet>
  );
}
